import { createSelector } from '@reduxjs/toolkit';

import { convertTimestampStrToMs } from '@/shared/lib/dateTime';
import { SegmentProcessingStep, SegmentStatus, TServerSegment } from '@/shared/types/Editor';
import { Region, RegionStatus } from '@/shared/types/Region';

import { RootState } from '../index';

const segmentsMapState = (state: RootState) => state.editor.segmentsMap;
const segmentsIdsState = (state: RootState) => state.editor.segmentsIds;
const focusedSegmentIdState = (state: RootState) => state.editor.focusedSegmentId;
const isAutoSavingState = (state: RootState) => state.editor.isAutoSaving;
const segmentsDataToSaveState = (state: RootState) => state.editor.segmentsDataToSave;

/** One mutation-admission policy for both UI selectors and token-owned editor operations. */
export const isEditorMutationBlocked = (state: RootState, ownedOperationId?: string): boolean =>
  Boolean(state.editor.activeHistoryTravel) ||
  state.editor.historyApplying ||
  Boolean(state.editor.activeEditorOperationId && state.editor.activeEditorOperationId !== ownedOperationId);

export const selectIsEditorMutationBlocked = (state: RootState): boolean => isEditorMutationBlocked(state);

export const isEditingSegmentSelector = createSelector([segmentsMapState, segmentsIdsState], (segments, segmentsIds) =>
  segmentsIds.some((id) => segments[id]?.isEditingSrc),
);

// True while a segment auto-save is in flight or queued — used to block transcript-version
// save/rollback until segment edits have settled.
export const selectIsSegmentSavePending = createSelector(
  [isAutoSavingState, segmentsDataToSaveState],
  (isAutoSaving, segmentsDataToSave) => isAutoSaving || Object.keys(segmentsDataToSave).length > 0,
);

// Transcript snapshots must not race either unsettled segment writes or an atomic editor/history
// transaction. All manual, background, and rollback entry points share this barrier.
export const selectIsTranscriptVersionActionBlocked = createSelector(
  [selectIsSegmentSavePending, selectIsEditorMutationBlocked, isEditingSegmentSelector],
  (isSegmentSavePending, editorMutationIsBlocked, isEditingSegment) =>
    isSegmentSavePending || editorMutationIsBlocked || isEditingSegment,
);

export const regionsSelector = createSelector([segmentsMapState, segmentsIdsState], (segments, segmentsIds): Region[] =>
  segmentsIds.reduce<Region[]>((acc, id) => {
    const segment = segments[id];

    if (!segment) {
      return acc;
    }

    let status: RegionStatus | undefined;

    if (segment.status === 'processing' || segment.isMerging) {
      status = 'loading';
    }

    if (segment.isSavingError) {
      status = 'error';
    }

    acc.push({
      id,
      end: convertTimestampStrToMs(segment.end) / 1000,
      needsAttention: Boolean(segment.needs_attention),
      speaker: segment.speaker,
      start: convertTimestampStrToMs(segment.start) / 1000,
      status,
      text: (segment.dst?.text ?? '').replace(/\n/g, ''),
    });

    return acc;
  }, []),
);

export const segmentByIdSelector = (id: string) =>
  createSelector(
    (state: RootState) => state.editor.segmentsMap[id],
    (segment) => segment,
    {
      devModeChecks: { identityFunctionCheck: 'never' },
    },
  );

export const segmentMergingAbilityByIdSelector = (id?: string) =>
  createSelector(
    (state: RootState) => id && state.editor.segmentsMap[id],
    (segment) =>
      segment ? !segment.isMerging && !segment.isEditingSrc && segment.status !== SegmentStatus.Processing : false,
  );

export const processingSegmentsIdsSelector = createSelector(
  [segmentsMapState, segmentsIdsState],
  (segments, segmentsIds) => {
    const indices: TServerSegment['id'][] = [];

    segmentsIds.forEach((segmentId) => {
      if (segments[segmentId]?.status === SegmentStatus.Processing) {
        indices.push(segmentId);
      }
    });

    return indices;
  },
);

export const isProcessingSegmentSelector = createSelector(
  processingSegmentsIdsSelector,
  (processingSegmentsIds) => processingSegmentsIds.length > 0,
);

export const isMergingSegmentsSelector = createSelector([segmentsMapState, segmentsIdsState], (segments, segmentsIds) =>
  segmentsIds.some((id) => segments[id]?.isMerging),
);

export const segmentWithoutTextsSelector = createSelector(
  [segmentsMapState, segmentsIdsState],
  (segments, segmentsIds) =>
    segmentsIds.filter((id) => segments[id] && !segments[id].src?.text && !segments[id].dst?.text),
);

export const segmentWithoutDstTextsSelector = createSelector(
  [segmentsMapState, segmentsIdsState],
  (segments, segmentsIds) =>
    segmentsIds.filter((id) => segments[id] && !segments[id].dst?.text && segments[id].src?.text),
);

export const segmentsThatBlockDubbingSelector = createSelector(
  [segmentWithoutDstTextsSelector, segmentWithoutTextsSelector],
  (segmentWithoutDstTexts, segmentWithoutTexts) => [...segmentWithoutDstTexts, ...segmentWithoutTexts],
);

export const processingSegmentsBreakdownSelector = createSelector(
  [segmentsMapState, segmentsIdsState],
  (segments, segmentsIds) => {
    let translating = 0;
    let voicing = 0;
    let unknown = 0;

    segmentsIds.forEach((id) => {
      if (segments[id]?.status === SegmentStatus.Processing) {
        const step = segments[id]?.processing_step;

        if (step === SegmentProcessingStep.Translating) translating++;
        else if (step === SegmentProcessingStep.Voicing) voicing++;
        else unknown++;
      }
    });

    return { translating, unknown, voicing };
  },
);

export const focusedSegmentStartSelector = createSelector(
  [segmentsMapState, focusedSegmentIdState],
  (segments, focusedSegmentId) => {
    const segment = focusedSegmentId ? segments[focusedSegmentId] : null;

    return segment ? segment.start : null;
  },
);
